import { apiClient } from './client';

/**
 * 공지 슬라이드 API (migration 172 · 시안 화면 E·J).
 *
 * backend: apps/chat-server/src/modules/notice-slide/notice-slide.routes.ts
 *
 * ★ 위젯이 부르는 것은 `listLiveSlides` 하나뿐이고, **거르기는 서버가 한다** —
 *   기한이 지난 슬라이드는 여기까지 오지 않는다. 화면이 감추는 방식이면 끝난 공지가
 *   브라우저까지 갔다가 사라진다(잠깐 보이고, 캐시에 남고, 앱이 다르게 그린다).
 *
 * ★ 그림은 base64 로 올리고 **주소로 받는다**(`imageUrl`). 저장소 키는 밖으로 내보내지 않는다 —
 *   나가면 그 뒤로 키 규칙을 못 바꾼다.
 */

export type SlideKind = 'IMAGE' | 'TEXT';
export type SlideTextPos = 'LEFT' | 'CENTER' | 'RIGHT';
export type SlideTransition = 'SLIDE' | 'FADE';

export interface NoticeSlideSetDto {
  id: string;
  name: string;
  autoPlay: boolean;
  intervalSec: number;
  transition: SlideTransition;
  maxSlides: number;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  /** 관리 목록에서만 — 전체 장수 · 지금 도는 장수. */
  slideCount?: number;
  liveCount?: number;
}

export interface NoticeSlideDto {
  id: string;
  setId: string;
  kind: SlideKind;
  title: string;
  summary: string | null;
  hasImage: boolean;
  imageUrl: string | null;
  imageW: number | null;
  imageH: number | null;
  imageBytes: number | null;
  scrim: boolean;
  textPos: SlideTextPos;
  tagLabel: string | null;
  tagColor: string | null;
  linkUrl: string | null;
  startsAt: string;
  endsAt: string;
  audience: string;
  sortOrder: number;
  viewCount: number;
}

export interface SlideImageInput {
  contentType: string;
  /** base64 (data URL 접두어 없이). */
  data: string;
  width?: number;
  height?: number;
}

export interface SlideWriteInput {
  kind: SlideKind;
  title: string;
  summary?: string | null;
  tagLabel?: string | null;
  tagColor?: string | null;
  linkUrl?: string | null;
  textPos?: SlideTextPos;
  scrim?: boolean;
  startsAt: string;
  endsAt: string;
  image?: SlideImageInput;
}

/** 위젯 — 지금 도는 것. 묶음을 안 주면 회사 기본 묶음. */
export async function listLiveSlides(setId?: string): Promise<{ set: NoticeSlideSetDto | null; slides: NoticeSlideDto[] }> {
  const { data } = await apiClient.get('/notice-slides', { params: setId ? { setId } : undefined });
  return data;
}

/* ── 관리 (통합설정 › 대시보드 › 공지 슬라이드) ─────────────────────────── */

export async function listSlideSets(): Promise<NoticeSlideSetDto[]> {
  const { data } = await apiClient.get('/admin/notice-slide-sets');
  return data.sets ?? [];
}

export async function createSlideSet(name: string, isDefault = false): Promise<NoticeSlideSetDto> {
  const { data } = await apiClient.post('/admin/notice-slide-sets', { name, isDefault });
  return data.set;
}

export async function updateSlideSet(
  id: string,
  patch: Partial<Pick<NoticeSlideSetDto, 'name' | 'autoPlay' | 'intervalSec' | 'transition' | 'maxSlides' | 'isDefault'>>,
): Promise<NoticeSlideSetDto> {
  const { data } = await apiClient.patch(`/admin/notice-slide-sets/${id}`, patch);
  return data.set;
}

export async function listSetSlides(setId: string): Promise<NoticeSlideDto[]> {
  const { data } = await apiClient.get(`/admin/notice-slide-sets/${setId}/slides`);
  return data.slides ?? [];
}

export async function createSlide(setId: string, input: SlideWriteInput): Promise<NoticeSlideDto> {
  const { data } = await apiClient.post('/admin/notice-slides', { setId, ...input });
  return data.slide;
}

export async function updateSlide(
  id: string,
  patch: Partial<SlideWriteInput> & { sortOrder?: number },
): Promise<NoticeSlideDto> {
  const { data } = await apiClient.patch(`/admin/notice-slides/${id}`, patch);
  return data.slide;
}

export async function deleteSlide(id: string): Promise<void> {
  await apiClient.delete(`/admin/notice-slides/${id}`);
}

/**
 * 고른 파일을 서버가 받는 모양으로 바꾼다.
 *
 * ★ 크기(width·height)를 여기서 재서 함께 보낸다 — 서버가 이미지를 해석하지 않기 때문이고
 *   (해석하면 그림 라이브러리가 서버로 들어온다), 관리 목록이 「1600×900」을 그대로 보여 준다.
 */
export function readImageFile(file: File): Promise<SlideImageInput> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('파일을 읽지 못했습니다'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      const base64 = comma >= 0 ? result.slice(comma + 1) : result;
      const img = new Image();
      img.onload = () => resolve({
        contentType: file.type, data: base64, width: img.naturalWidth, height: img.naturalHeight,
      });
      // 크기를 못 재도 등록 자체는 막지 않는다 — 크기는 «곁말» 이지 조건이 아니다.
      img.onerror = () => resolve({ contentType: file.type, data: base64 });
      img.src = result;
    };
    reader.readAsDataURL(file);
  });
}
